/**
 * @file GtmService.ts
 *
 * This is a typed implementation of a Google Tag Manager client using the Google Analytics v4.0 API for ecommerce
 * events.
 *
 * see: https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm
 *
 * The following must be added to the html HEAD as early as possible for this class to work...

        <!-- Google Tag Manager -->
        <script>
            (function (w, d, s, l, i) {
                w[l] = w[l] || [];
                w[l].push({
                    'gtm.start': new Date().getTime(),
                    event: 'gtm.js',
                });
                var f = d.getElementsByTagName(s)[0],
                    j = d.createElement(s),
                    dl = l != 'dataLayer' ? '&l=' + l : '';
                j.async = true;
                j.src =
                    'https://www.googletagmanager.com/gtm.js?id=' +
                    i +
                    dl +
                    '&gtm_auth=GTM_AUTH&gtm_preview=GTM_PREVIEW&gtm_cookies_win=x';
                f.parentNode.insertBefore(j, f);
            })(window, document, 'script', 'dataLayer', 'GTM_CONTAINER_ID');
        </script>
        <!-- End Google Tag Manager -->

 * and this will need to be added immediately after the opening BODY tag...

        <!-- Google Tag Manager (noscript) -->
        <noscript
            ><iframe
                src="https://www.googletagmanager.com/ns.html?id=GTM_CONTAINER_ID&gtm_auth=GTM_AUTH&gtm_preview=GTM_PREVIEW&gtm_cookies_win=x"
                height="0"
                width="0"
                style="display: none; visibility: hidden"
            ></iframe
        ></noscript>
        <!-- End Google Tag Manager (noscript) -->

 * string replacement will need to be working on html for GTM_AUTH and GTM_PREVIEW
 */

declare global {
    interface Window {
        dataLayer: Record<string, unknown>[]
    }
}

export type GA4Events =
    | 'add_payment_info'
    | 'add_shipping_info'
    | 'begin_checkout'
    | 'purchase'
    | 'view_cart'


export interface GA4Item {
    item_id: string;
    item_name: string;
    item_brand?: string; // foundry
    item_category?: string; //
    item_category2?: string;
    item_category3?: string;
    item_category4?: string;
    item_category5?: string;
    item_variant?: string;
    price?: number;
    quantity?: number;
    coupon?: string; // The coupon name/code (if any) associated with an individual item
    index?: number; // The index/position of the item in a list.
    item_list_name?: string;
    item_list_id?: string;
    discount?: number;
    affiliation?: string; // partner/vendor not applicable to TN at this time since we do not do any affilitate marketing or sell through third party outlets.
    location_id?: string;
    promotion_id?: string;
    promotion_name?: string;
    creative_name?: string;
    creative_slot?: string;
}


export interface GA4EventParams {
    // ISO 2417 currency code, see: https://en.wikipedia.org/wiki/ISO_4217
    currency?: string;
    value?: number;
    items?: GA4Item[];
    promotion_id?: string;
    promotion_name?: string;
    creative_name?: string;
    creative_slot?: string;
}

export interface GA4EcommerceEventParams extends GA4EventParams {
    currency: string;
    value: number;
    items: GA4Item[];
}


// #region event specific params

export interface GA4AddPaymentInfoEventParams extends GA4EventParams {
    payment_type: string;
}

export interface GA4PurchaseEventParams extends GA4EcommerceEventParams {
    transaction_id: string;
    affiliation?: string;
    coupon?: string;
    shipping?: number;
    tax?: number;
}
// #endregion

export class GtmService {
    add_payment_info(params: GA4AddPaymentInfoEventParams): void {
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({ event: 'add_payment_info', ecommerce: params });
    }

    add_shipping_info(params: GA4EcommerceEventParams): void {
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({ event: 'add_shipping_info', ecommerce: params });
    }

    begin_checkout(params: GA4EcommerceEventParams): void {
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({ event: 'begin_checkout', ecommerce: params });
    }

    purchase(params: GA4PurchaseEventParams): void {
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({ event: 'purchase', ecommerce: params });
    }

    view_cart(params: GA4EcommerceEventParams): void {
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({ event: 'view_cart', ecommerce: params });
    }
}
